import {
  StandardRate,
  StandardRateHistory,
} from "us.collection.admin/interfaces"; 
import _ from "lodash";
import dayjs from "dayjs";

/**
 * @description Format standard rates.
 * @param {Array} standardRateResponse - Standard rates response
 * @returns {Array} - Updated standard rates
 */
export const formatStandardRates = (standardRateResponse: {
  status: number;
  data: Array<StandardRate>;
}): Array<StandardRate> => {
  try {
    const { status, data: standardRates } = standardRateResponse ?? {};

    if (status == 200 && Array.isArray(standardRates)) {
      const currentDate = dayjs();

      // Filter rates that are effective before or on the current date
      const filteredRates = standardRates.filter(
        (rate) => dayjs(rate.effectiveDate) <= currentDate
      );

      // Sort the filtered rates by effective date in descending order
      filteredRates.sort((a, b) =>
        dayjs(b.effectiveDate).diff(dayjs(a.effectiveDate))
      );

      // Get the current standard rate
      const currentStandardRateId = filteredRates[0].rateId;

      return standardRates.map((standardRate) => ({
        ...standardRate,
        isCurrentRate: currentStandardRateId == standardRate.rateId,
      }));
    } else {
      return [];
    }
  } catch (error) {
    return Array.isArray(standardRateResponse.data)
      ? standardRateResponse.data
      : [];
  }
};

/**
 * @description Group and sort standard rate history.
 * @param {Array} ratesHistoryResponse - Standard rates history response
 * @returns {Array} - Updated standard rates history
 */
export const sortStandardRatesHistory = (ratesHistoryResponse: {
  status: number;
  data: Array<StandardRateHistory>;
}): Array<StandardRateHistory> => {
  try {
    const { status, data: ratesHistory } = ratesHistoryResponse ?? {};

    if (status == 200 && Array.isArray(ratesHistory)) {
      const newHistory = _(ratesHistory)
        .groupBy("rateId")
        .map((group) => _.orderBy(group, "modifiedDate", "desc"))
        .flatten()
        .value();

      return Array.isArray(newHistory)
        ? _.orderBy(newHistory, "historyId", "desc")
        : ratesHistory;
    } else {
      return [];
    }
  } catch (error) {
    return Array.isArray(ratesHistoryResponse.data)
      ? ratesHistoryResponse.data
      : [];
  }
};

/**
 * @description Disable delete option.
 * @param {string} effectiveDate - Current effective date.
 * @returns {boolean} - Whether the option is enabled.
 */
export const isDeleteDisabled = (effectiveDate: string): boolean => {
  try {
    if (effectiveDate) {
      return dayjs(effectiveDate).isBefore(dayjs().subtract(1, "day"));
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};
